import { useEffect, useState, useCallback } from 'react';
import {
  Download, Loader2, AlertCircle, Save, ExternalLink, RotateCcw, Monitor, Smartphone,
} from 'lucide-react';
import {
  getClientDownloadAdmin, updateClientDownload,
  type ClientDownloadAdmin, type ClientDownloadPlatform,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { invalidateClientDownloadCache } from '@/shared/hooks/useClientDownload';

interface FormState {
  url: string;
  label: string;
  version: string;
  sizeMb: string;   // string p/ permitir input vazio
  notes: string;
}

// Cabal Next (dono, 2026-10-20): um link por plataforma. Url vazia = botao "Em breve" na home.
const PLATFORMS: {
  id: ClientDownloadPlatform;
  title: string;
  icon: typeof Monitor;
  defaultLabel: string;
  placeholder: string;
}[] = [
  { id: 'pc', title: 'PC (Windows)', icon: Monitor, defaultLabel: 'Download PC', placeholder: 'https://.../CabalNext-Setup.exe' },
  { id: 'android', title: 'Android (APK)', icon: Smartphone, defaultLabel: 'Download Android', placeholder: 'https://.../CabalNext.apk' },
];

export default function ClientDownloadPage() {
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Download className="text-amber-400" size={26} />
            Links de Download do Client
          </h1>
          <p className="text-sm text-white/65 mt-1">
            Botões da home (PC e Android) · vazio = "Em breve" · só baixa quem está logado ·
            EventData.web_client_download
          </p>
        </div>
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold
                     bg-white/[0.04] hover:bg-white/[0.08] text-white/70
                     border border-white/[0.08] transition cursor-pointer"
        >
          <RotateCcw size={14} />
          Recarregar
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {PLATFORMS.map((p) => (
          <PlatformCard key={`${p.id}-${reloadKey}`} platform={p} />
        ))}
      </div>
    </div>
  );
}

function PlatformCard({ platform }: { platform: (typeof PLATFORMS)[number] }) {
  const { notify } = useToast();
  const emptyForm: FormState = { url: '', label: platform.defaultLabel, version: '', sizeMb: '', notes: '' };
  const [data, setData] = useState<ClientDownloadAdmin | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const Icon = platform.icon;

  const fillForm = (d: ClientDownloadAdmin) => {
    setForm({
      url: d.url || '',
      label: d.label || platform.defaultLabel,
      version: d.version || '',
      sizeMb: d.sizeMb != null ? String(d.sizeMb) : '',
      notes: d.notes || '',
    });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getClientDownloadAdmin(platform.id);
      setData(d);
      fillForm(d);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const sizeNum = form.sizeMb.trim() === '' ? null : Number(form.sizeMb);
      const payload = {
        url: form.url.trim(),
        label: form.label.trim() || platform.defaultLabel,
        version: form.version.trim(),
        sizeMb: sizeNum != null && Number.isFinite(sizeNum) ? sizeNum : null,
        notes: form.notes.trim() || null,
      };
      const d = await updateClientDownload(payload, platform.id);
      setData(d);
      fillForm(d);
      invalidateClientDownloadCache();
      notify('success', `Link ${platform.title} atualizado`);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const dirty = data ? (
    form.url !== (data.url || '') ||
    form.label !== (data.label || platform.defaultLabel) ||
    form.version !== (data.version || '') ||
    form.sizeMb !== (data.sizeMb != null ? String(data.sizeMb) : '') ||
    form.notes !== (data.notes || '')
  ) : false;

  return (
    <div className="surface-card rounded-xl p-6 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Icon size={20} className="text-amber-400" />
          {platform.title}
        </h2>
        <span
          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
            data?.url
              ? 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10'
              : 'text-white/55 border-white/[0.12] bg-white/[0.04]'
          }`}
        >
          {data?.url ? 'Disponível na home' : 'Em breve'}
        </span>
      </div>

      {error && (
        <div className="rounded-lg p-3 flex items-start gap-3 border border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {loading && !data ? (
        <div className="py-12 flex items-center justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <>
          <Field label="URL do download" hint='Link direto. Vazio = o botão fica "Em breve" na home.'>
            <div className="flex gap-2">
              <input
                type="url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder={platform.placeholder}
                className="flex-1 min-w-0 bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2
                           text-sm text-white placeholder-white/30 focus:border-amber-400/40
                           focus:outline-none"
              />
              {form.url && (
                <a
                  href={form.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold
                             bg-white/[0.04] hover:bg-white/[0.08] text-white/70
                             border border-white/[0.08] transition cursor-pointer"
                  title="Testar link"
                >
                  <ExternalLink size={14} />
                  Testar
                </a>
              )}
            </div>
          </Field>

          <Field label="Label do botao" hint="Texto do botão nas páginas antigas (a home do Next usa texto fixo).">
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2
                         text-sm text-white placeholder-white/30 focus:border-amber-400/40
                         focus:outline-none"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Versao" hint="Ex: 187">
              <input
                type="text"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2
                           text-sm text-white placeholder-white/30 focus:border-amber-400/40
                           focus:outline-none"
              />
            </Field>
            <Field label="Tamanho (MB)" hint="Opcional. So numero.">
              <input
                type="number"
                min={0}
                value={form.sizeMb}
                onChange={(e) => setForm({ ...form, sizeMb: e.target.value })}
                className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2
                           text-sm text-white placeholder-white/30 focus:border-amber-400/40
                           focus:outline-none"
              />
            </Field>
          </div>

          <Field label="Observacao" hint="Opcional (nota interna / páginas antigas).">
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2
                         text-sm text-white placeholder-white/30 focus:border-amber-400/40
                         focus:outline-none resize-y"
            />
          </Field>

          <div className="flex items-center justify-between pt-3 border-t border-white/[0.05]">
            <div className="text-[11px] text-white/45">
              {data?.updatedAt && (
                <>
                  Atualizado em <span className="text-white/70">
                    {new Date(data.updatedAt).toLocaleString('pt-BR')}
                  </span>
                  {data.updatedBy != null && <> · por user #{data.updatedBy}</>}
                </>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                         btn-primary
                         transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Salvar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({
  label, required, hint, children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-white/75 mb-1.5">
        {label}
        {required && <span className="text-amber-400"> *</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-white/40 mt-1">{hint}</span>}
    </label>
  );
}
